import "./booking-calendar.scss";

import { GetClinicScheduleDto, GetScheduleExceptionDto } from "@api/api";
import { useQyGetPublicWeeklySchedule } from "@factory/query/clinic-schedule.query";
import {
  useQyGetPublicNextAvailableDate,
  useQyGetPublicUpcomingExceptions,
} from "@factory/query/schedule-exception.query";
import { Button } from "primereact/button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface BookingCalendarProps {
  tenant: string;
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
}

interface DayInfo {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isPast: boolean;
  isToday: boolean;
  isSelected: boolean;
  status: "available" | "closed" | "fully-booked" | "unknown";
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function BookingCalendar({
  tenant,
  onDateSelect,
  selectedDate,
}: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const hasSetDefaultDate = useRef(false);

  const { data: weeklySchedule, isLoading: isLoadingSchedule } =
    useQyGetPublicWeeklySchedule(tenant);

  const { data: exceptions, isLoading: isLoadingExceptions } =
    useQyGetPublicUpcomingExceptions(tenant, 999);

  const { data: nextAvailableData } = useQyGetPublicNextAvailableDate(tenant);

  useEffect(() => {
    if (nextAvailableData && !hasSetDefaultDate.current) {
      const dateStr =
        nextAvailableData.next_available_date || nextAvailableData.default_date;
      if (dateStr) {
        const targetDate = new Date(dateStr);
        setCurrentMonth(
          new Date(targetDate.getFullYear(), targetDate.getMonth(), 1),
        );
        hasSetDefaultDate.current = true;
      }
    }
  }, [nextAvailableData]);

  const scheduleByDay = useMemo(() => {
    if (!weeklySchedule) return new Map<number, GetClinicScheduleDto>();
    return new Map(weeklySchedule.map((s) => [s.day_of_week, s]));
  }, [weeklySchedule]);

  const exceptionsByDate = useMemo(() => {
    if (!exceptions) return new Map<string, GetScheduleExceptionDto>();
    return new Map(exceptions.map((e) => [e.exception_date, e]));
  }, [exceptions]);

  const getDaysInMonth = useCallback(
    (year: number, month: number): DayInfo[] => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startingDayOfWeek = firstDay.getDay();
      const daysInMonth = lastDay.getDate();

      const days: DayInfo[] = [];

      // Add empty cells for days before the first day of the month
      for (let i = 0; i < startingDayOfWeek; i++) {
        days.push({
          date: new Date(year, month, -startingDayOfWeek + i + 1),
          dayNumber: 0,
          isCurrentMonth: false,
          isPast: true,
          isToday: false,
          isSelected: false,
          status: "unknown",
        });
      }

      // Add days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        date.setHours(0, 0, 0, 0);
        // Format date in local timezone to avoid UTC conversion issues
        const dateYear = date.getFullYear();
        const dateMonth = String(date.getMonth() + 1).padStart(2, "0");
        const dateDay = String(date.getDate()).padStart(2, "0");
        const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;
        const dayOfWeek = date.getDay();

        const isPast = date < today;
        const isToday = date.getTime() === today.getTime();
        const isSelected = selectedDate
          ? date.getTime() ===
            new Date(selectedDate.setHours(0, 0, 0, 0)).getTime()
          : false;

        // Check for exceptions first
        const exception = exceptionsByDate.get(dateStr);
        let status: DayInfo["status"] = "unknown";

        if (exception) {
          if (
            exception.is_full_day_closure ||
            exception.exception_type === "HOLIDAY" ||
            exception.exception_type === "CLOSURE"
          ) {
            status = "closed";
          } else {
            status = "available"; // Special hours
          }
        } else {
          // Check regular schedule
          const schedule = scheduleByDay.get(dayOfWeek);
          if (schedule) {
            status = schedule.is_closed ? "closed" : "available";
          } else {
            status = "closed"; // No schedule defined = closed
          }
        }

        days.push({
          date,
          dayNumber: day,
          isCurrentMonth: true,
          isPast,
          isToday,
          isSelected,
          status,
        });
      }

      // Fill remaining cells to complete the grid
      const remainingCells = 42 - days.length;
      for (let i = 1; i <= remainingCells; i++) {
        days.push({
          date: new Date(year, month + 1, i),
          dayNumber: i,
          isCurrentMonth: false,
          isPast: false,
          isToday: false,
          isSelected: false,
          status: "unknown",
        });
      }

      return days;
    },
    [scheduleByDay, exceptionsByDate, selectedDate],
  );

  const calendarDays = useMemo(() => {
    return getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [currentMonth, getDaysInMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  const handleDayClick = (day: DayInfo) => {
    if (
      day.isPast ||
      !day.isCurrentMonth ||
      day.status === "closed" ||
      day.status === "fully-booked"
    ) {
      return;
    }
    onDateSelect(day.date);
  };

  const isPrevDisabled = useMemo(() => {
    const today = new Date();
    return (
      currentMonth.getFullYear() === today.getFullYear() &&
      currentMonth.getMonth() <= today.getMonth()
    );
  }, [currentMonth]);

  const upcomingClosures = useMemo(() => {
    if (!exceptions) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return exceptions
      .filter((e) => {
        const exDate = new Date(e.exception_date);
        return (
          exDate >= today &&
          (e.is_full_day_closure || e.exception_type !== "SPECIAL_HOURS")
        );
      })
      .slice(0, 3);
  }, [exceptions]);

  const formatClosureDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const isLoading = isLoadingSchedule || isLoadingExceptions;

  return (
    <div className="booking-calendar">
      <div className="calendar-header">
        <div className="month-navigation">
          <button
            className="nav-btn"
            onClick={handlePrevMonth}
            disabled={isPrevDisabled}
            aria-label="Previous month"
          >
            <i className="pi pi-chevron-left"></i>
          </button>
          <h3>
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            className="nav-btn"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            <i className="pi pi-chevron-right"></i>
          </button>
        </div>
        <Button
          label="Today"
          className="p-button-text p-button-sm today-btn"
          onClick={handleToday}
        />
      </div>

      <div className="weekday-header">
        {WEEKDAYS.map((day) => (
          <div key={day} className="weekday">
            {day}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="loading-skeleton">
          <div className="skeleton-grid">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="skeleton-day"></div>
            ))}
          </div>
        </div>
      ) : (
        <div className="calendar-grid">
          {calendarDays.map((day, index) => {
            const classNames = [
              "calendar-day",
              !day.isCurrentMonth ? "empty" : "",
              day.isPast && day.isCurrentMonth ? "past" : "",
              day.isToday ? "today" : "",
              day.isSelected ? "selected" : "",
              day.isCurrentMonth && !day.isPast ? day.status : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <div
                key={index}
                className={classNames}
                onClick={() => day.isCurrentMonth && handleDayClick(day)}
                role="button"
                aria-label={
                  day.isCurrentMonth
                    ? `${day.dayNumber} ${MONTHS[currentMonth.getMonth()]}, ${day.status}`
                    : undefined
                }
                tabIndex={
                  day.isCurrentMonth &&
                  !day.isPast &&
                  day.status === "available"
                    ? 0
                    : -1
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleDayClick(day);
                  }
                }}
              >
                {day.isCurrentMonth && (
                  <>
                    <span className="day-number">{day.dayNumber}</span>
                    {!day.isPast && <span className="status-dot"></span>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="legend">
        <div className="legend-item">
          <span className="legend-dot available"></span>
          <span>Available</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot closed"></span>
          <span>Closed</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot fully-booked"></span>
          <span>Fully Booked</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot selected"></span>
          <span>Selected</span>
        </div>
      </div>

      {upcomingClosures.length > 0 && (
        <div className="upcoming-closures">
          <div className="closures-title">
            <i className="pi pi-info-circle"></i>
            Upcoming Closures
          </div>
          <div className="closure-list">
            {upcomingClosures.map((closure) => (
              <div key={closure.code} className="closure-item">
                <span className="closure-date">
                  {formatClosureDate(closure.exception_date)}
                </span>
                <span className="closure-reason">— {closure.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingCalendar;
